import React, { useState, useEffect } from 'react';
import { remindersAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Bell, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28];
// Common pre-due offsets for compliance work (days before due date)
const OFFSET_PRESETS = [30, 21, 14, 10, 7, 5, 3, 2, 1, 0];

const ordinalSuffix = (d) => {
  if (d >= 11 && d <= 13) return `${d}th`;
  const last = d % 10;
  if (last === 1) return `${d}st`;
  if (last === 2) return `${d}nd`;
  if (last === 3) return `${d}rd`;
  return `${d}th`;
};

const ReminderDialog = ({ open, onOpenChange, taskId, subtaskId, subtaskTitle, frequency, dueDateRule }) => {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedOffsets, setSelectedOffsets] = useState([]); // days BEFORE due date
  const [mode, setMode] = useState('day_of_month'); // 'day_of_month' | 'days_before_due'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    remindersAPI.list({ subtask_id: subtaskId })
      .then(res => {
        const match = (res.data || []).find(r => r.active !== false);
        setExisting(match || null);
        if (match) {
          setSelectedDays(match.schedule_days || []);
          setSelectedOffsets(match.offset_days || []);
          // Default mode based on which set is populated
          if ((match.offset_days || []).length > 0 && (match.schedule_days || []).length === 0) {
            setMode('days_before_due');
          } else {
            setMode('day_of_month');
          }
        } else {
          setSelectedDays([]);
          setSelectedOffsets([]);
          setMode('day_of_month');
        }
      })
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, [open, subtaskId]); // remindersAPI, logger are stable module-level refs

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const toggleOffset = (n) => {
    setSelectedOffsets(prev =>
      prev.includes(n) ? prev.filter(d => d !== n) : [...prev, n].sort((a, b) => b - a) // descending: 30, 14, 7...
    );
  };

  const handleSave = async () => {
    // Validate based on mode
    if (mode === 'day_of_month' && selectedDays.length === 0) {
      toast.error('Select at least one day of the month');
      return;
    }
    if (mode === 'days_before_due' && selectedOffsets.length === 0) {
      toast.error('Select at least one offset (days before due date)');
      return;
    }
    // Build payload — clear the unused mode's array so saving "day_of_month"
    // wipes any previously-saved offset_days, and vice versa.
    const payload = mode === 'day_of_month'
      ? { schedule_days: selectedDays, offset_days: [] }
      : { schedule_days: [], offset_days: selectedOffsets };

    setSubmitting(true);
    try {
      if (existing) {
        await remindersAPI.update(existing.id, { ...payload, active: true });
        toast.success('Reminder schedule updated');
      } else {
        await remindersAPI.create({ task_id: taskId, subtask_id: subtaskId, ...payload });
        toast.success('Reminder schedule set');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save reminder');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!existing) return;
    setSubmitting(true);
    try {
      await remindersAPI.delete(existing.id);
      toast.success('Reminder removed');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
    setSubmitting(false);
  };

  const handleToggleActive = async () => {
    if (!existing) return;
    setSubmitting(true);
    try {
      await remindersAPI.update(existing.id, { active: !existing.active });
      toast.success(existing.active ? 'Reminder paused' : 'Reminder activated');
      setExisting(prev => ({ ...prev, active: !prev.active }));
    } catch (err) {
      toast.error('Failed to toggle reminder');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4" /> Set Reminder Schedule
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-sm font-medium">{subtaskTitle}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Reminders will be sent to all clients whose documents are pending for this subtask.
              </p>
              {existing && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={existing.active ? 'default' : 'secondary'} className="text-[10px]">
                    {existing.active ? 'Active' : 'Paused'}
                  </Badge>
                  {existing.last_processed && (
                    <span className="text-[10px] text-muted-foreground">
                      Last sent: {new Date(existing.last_processed).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-1.5 border rounded-md p-0.5 bg-muted/30" data-testid="reminder-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('day_of_month')}
                className={`flex-1 text-xs py-1.5 rounded transition-colors ${mode === 'day_of_month' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="mode-day-of-month"
              >
                By day of month
              </button>
              <button
                type="button"
                onClick={() => setMode('days_before_due')}
                className={`flex-1 text-xs py-1.5 rounded transition-colors ${mode === 'days_before_due' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="mode-days-before-due"
              >
                Days before due date
              </button>
            </div>

            {mode === 'day_of_month' ? (
              <div>
                <p className="text-xs font-medium mb-2">Select days of month to send reminders</p>
                <p className="text-[10px] text-muted-foreground mb-3">
                  {(() => {
                    const f = (frequency || 'monthly').toLowerCase();
                    if (f === 'monthly') return 'On each selected day of every month, all clients with pending documents for the current period will receive a reminder in their chat thread.';
                    if (f === 'quarterly') return "On each selected day during the quarter's due-date month only — not every month. Pending clients receive a chat reminder. (Configure the Due Date Settings panel first so we know which month each quarter is due in.)";
                    if (f === 'half_yearly') return "On each selected day during the half-year's due-date month only — not every month. Pending clients receive a chat reminder. (Configure the Due Date Settings panel first.)";
                    return "On each selected day during the annual due-date month only — not every month. Pending clients receive a chat reminder. (Configure the Due Date Settings panel first.)";
                  })()}
                </p>
                <div className="grid grid-cols-7 gap-1.5">
                  {DAY_OPTIONS.map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      className={`h-8 w-full rounded text-xs font-medium transition-colors border
                        ${selectedDays.includes(day)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-muted border-border text-muted-foreground hover:text-foreground'
                        }`}
                      data-testid={`day-${day}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-xs font-medium mb-2">Send reminder N days before due date</p>
                <p className="text-[10px] text-muted-foreground mb-3">
                  Reminders fire automatically the configured number of days before each period's due date — no calendar math, even when the date shifts year-on-year. Pick one or more lead-times below, or type a custom value.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {OFFSET_PRESETS.map(n => (
                    <button
                      key={n}
                      onClick={() => toggleOffset(n)}
                      className={`px-3 h-8 rounded-full text-xs font-medium transition-colors border
                        ${selectedOffsets.includes(n)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-muted border-border text-muted-foreground hover:text-foreground'
                        }`}
                      data-testid={`offset-${n}`}
                    >
                      {n === 0 ? 'On due date' : `${n} day${n === 1 ? '' : 's'} before`}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={90}
                    placeholder="Custom (e.g. 45)"
                    className="h-8 w-32 px-2 text-xs border rounded"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const n = parseInt(e.currentTarget.value, 10);
                        if (Number.isInteger(n) && n >= 1 && n <= 90) {
                          if (!selectedOffsets.includes(n)) toggleOffset(n);
                          e.currentTarget.value = '';
                        }
                      }
                    }}
                    data-testid="offset-custom-input"
                  />
                  <span className="text-[10px] text-muted-foreground">Press Enter to add</span>
                </div>
              </div>
            )}

            {((mode === 'day_of_month' && selectedDays.length > 0) || (mode === 'days_before_due' && selectedOffsets.length > 0)) && (
              <div className="bg-muted/20 rounded-lg p-2.5">
                <p className="text-[10px] text-muted-foreground mb-1.5">Reminder schedule</p>
                <p className="text-xs font-medium">
                  {(() => {
                    if (mode === 'days_before_due') {
                      const offsets = [...selectedOffsets].sort((a, b) => b - a);
                      const labels = offsets.map(n => n === 0 ? 'on due date' : `${n}d before`);
                      return `Fires ${labels.join(', ')} — once per ${(frequency || 'period').replace('_', '-')}`;
                    }
                    const f = (frequency || 'monthly').toLowerCase();
                    const days = selectedDays.map(d => ordinalSuffix(d)).join(', ');
                    if (f === 'monthly') return `${days} of every month`;
                    if (f === 'quarterly') {
                      const months = ['Q1', 'Q2', 'Q3', 'Q4']
                        .map(q => dueDateRule?.[q]?.month)
                        .filter(m => m);
                      const monthLabel = months.length
                        ? months.map(m => new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'short' })).join(', ')
                        : 'each quarter\u2019s due-date month';
                      return `${days} of ${monthLabel} (one cycle per quarter)`;
                    }
                    if (f === 'half_yearly') {
                      const months = ['H1', 'H2']
                        .map(h => dueDateRule?.[h]?.month)
                        .filter(m => m);
                      const monthLabel = months.length
                        ? months.map(m => new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'short' })).join(' & ')
                        : 'each half\u2019s due-date month';
                      return `${days} of ${monthLabel} (one cycle per half)`;
                    }
                    const m = dueDateRule?.month;
                    const monthLabel = m ? new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'long' }) : 'the due-date month';
                    return `${days} of ${monthLabel} only (once a year)`;
                  })()}
                </p>
                {(() => {
                  const f = (frequency || 'monthly').toLowerCase();
                  // Both modes need due_date_rule for non-monthly subtasks (offsets always need it)
                  const needsRule = (mode === 'days_before_due') || (f !== 'monthly');
                  if (!needsRule) return null;
                  let configured = false;
                  if (f === 'quarterly') configured = ['Q1','Q2','Q3','Q4'].some(q => dueDateRule?.[q]?.month);
                  else if (f === 'half_yearly') configured = ['H1','H2'].some(h => dueDateRule?.[h]?.month);
                  else if (f === 'monthly') configured = !!dueDateRule?.day; // monthly rule shape uses `day`
                  else configured = !!dueDateRule?.month;
                  if (!configured) {
                    return (
                      <p className="text-[10px] text-amber-700 mt-1.5 flex items-start gap-1">
                        <span aria-hidden>⚠</span>
                        <span>Due Date Settings not configured. Reminders will be held until you save a due-date rule for this {f.replace('_', '-')} subtask.</span>
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {existing && (
              <div className="flex items-center justify-between pt-1 border-t">
                <div className="flex items-center gap-2">
                  <Switch checked={existing.active} onCheckedChange={handleToggleActive} disabled={submitting} data-testid="reminder-toggle" />
                  <span className="text-xs text-muted-foreground">{existing.active ? 'Active' : 'Paused'}</span>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive h-7 text-xs gap-1" onClick={handleDelete} disabled={submitting} data-testid="delete-reminder-btn">
                  <Trash2 className="h-3 w-3" /> Remove
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={submitting || loading} data-testid="save-reminder-btn">
            {submitting ? 'Saving...' : (existing ? 'Update Schedule' : 'Set Schedule')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderDialog;
